import { Container, Heading } from '@seek/ai-elements';
import { Box, Stack, IconAI, Divider, Button, Text } from 'braid-design-system';
import { useCallback } from 'react';

import SkeletonLine from 'src/components/SkeletonLine/SkeletonLine';

import { CareerFeedAssistantQuestions } from './CareerFeedAssistantQuestions/CareerFeedAssistantQuestions';
import { useCareerFeedAIAssistant } from './hooks/useCareerFeedAIAssistant';
import type { CareerFeedMessage, CareerFeedTextMessage } from './types';

const CareerFeedAiAssistantInitialState = ({
  isCreateSessionLoading,
  isCreateSessionError,
  startSession,
}: {
  isCreateSessionLoading: boolean;
  isCreateSessionError: boolean;
  startSession: () => void;
}) => (
  <>
    <Text size="small">
      Fine-tune your job recommendations – tell us what you&apos;re looking for
      with seekAI
    </Text>
    {isCreateSessionError ? (
      <Text tone="critical">An error occurred. Please try again</Text>
    ) : null}
    <Divider />
    <Button
      tone="brandAccent"
      icon={<IconAI />}
      loading={isCreateSessionLoading}
      onClick={startSession}
    >
      Start fine-tuning
    </Button>
  </>
);

const CareerFeedAIAssistantContent = ({
  conversationFinished,
  sessionId,
  messages,
  startSession,
  isCreateSessionLoading,
  invokeSendMessage,
  invokeSendFeedback,
  isRefreshLoading,
  onRefreshRecommendations,
  isSendMessageError,
  isCreateSessionError,
  hasRefreshRecs,
}: {
  sessionId: string | null;
  conversationFinished: boolean;
  messages: CareerFeedMessage[];
  startSession: () => void;
  isCreateSessionLoading: boolean;
  invokeSendMessage: (message: string, skip: boolean) => void;
  invokeSendFeedback: (sentiment: boolean) => void;
  onRefreshRecommendations: () => void;
  isRefreshLoading: boolean;
  isCreateSessionError: boolean;
  isSendMessageError: boolean;
  hasRefreshRecs: boolean;
}) => {
  if (hasRefreshRecs) {
    return (
      <Text>
        We&apos;ve updated your recommendations based on your answers. Take a
        look at the personalised job listings.
      </Text>
    );
  }

  if (conversationFinished) {
    const lastAssistantMessage = messages
      .filter((message) => message.role === 'assistant')
      .filter((message): message is CareerFeedTextMessage => !message.loading)
      .slice(-1)[0];

    return (
      <Stack space="medium">
        <Stack space="xsmall">
          {lastAssistantMessage.text.split('\n').map((line, index) => (
            <Text key={index}>{line}</Text>
          ))}
        </Stack>
        <Divider />
        <Button
          tone="brandAccent"
          icon={<IconAI />}
          onClick={onRefreshRecommendations}
          loading={isRefreshLoading}
        >
          Refresh recommendations
        </Button>
      </Stack>
    );
  }

  return (
    <Stack space="medium">
      {!sessionId ? (
        <CareerFeedAiAssistantInitialState
          isCreateSessionLoading={isCreateSessionLoading}
          startSession={startSession}
          isCreateSessionError={isCreateSessionError}
        />
      ) : (
        <CareerFeedAssistantQuestions
          messages={messages}
          onSendMessage={invokeSendMessage}
          hasError={isSendMessageError}
          onSendFeedback={invokeSendFeedback}
        />
      )}
    </Stack>
  );
};

/** *
 * This component is responsible for displaying the AI Assistant for the Career Feed.
 * This is still in in discovery phase and the implementation of this component might change drastically.
 * The reason why this is in the code base is so that people can start playing around with it
 * and get feedback on the implementation.
 */
export const CareerFeedAIAssistantV2 = () => {
  const {
    invokeCreateSession,
    invokeSendMessage,
    invokeSendFeedback,
    refreshRecommendations,
    isRecommendationError,
    isRecommendationLoading,
    isCreateSessionLoading,
    sessionId,
    messages,
    isRefreshLoading,
    conversationFinished,
    hasRefreshRecs,
    isCreateSessionError,
    isSendMessageError,
  } = useCareerFeedAIAssistant();

  const startSession = useCallback(() => {
    invokeCreateSession();
  }, [invokeCreateSession]);

  if (isRecommendationError) {
    return null;
  }

  if (isRecommendationLoading) {
    return (
      <Box marginTop="large">
        <Container borderWidth="standard" padding="medium">
          <Stack space="medium">
            <SkeletonLine type="text" width={200} size="small" />
            <SkeletonLine type="text" width={260} size="xsmall" />
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box marginTop="large">
      <Container padding="medium" borderWidth="standard">
        <Stack space="medium">
          <Heading level="4" icon={<IconAI />}>
            Get better job matches
          </Heading>
          <CareerFeedAIAssistantContent
            conversationFinished={conversationFinished}
            sessionId={sessionId}
            messages={messages}
            startSession={startSession}
            isCreateSessionLoading={isCreateSessionLoading}
            invokeSendMessage={invokeSendMessage}
            invokeSendFeedback={invokeSendFeedback}
            isRefreshLoading={isRefreshLoading}
            onRefreshRecommendations={refreshRecommendations}
            isSendMessageError={isSendMessageError}
            isCreateSessionError={isCreateSessionError}
            hasRefreshRecs={hasRefreshRecs}
          />
        </Stack>
      </Container>
    </Box>
  );
};
